import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import InfoTooltip from '../components/InfoTooltip';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import api from '../utils/api';

// Format number with Danish thousand separator
const formatDKK = (num) => {
  if (num === null || num === undefined) return '0';
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Month names in Danish
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

// Aggregate weekly data into monthly data
const aggregateToMonthly = (weeklyData) => {
  if (!weeklyData || weeklyData.length === 0) return [];
  
  const monthlyMap = {};
  
  weeklyData.forEach(week => {
    const weekStart = new Date(week.week_start);
    const year = weekStart.getFullYear();
    const month = weekStart.getMonth();
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
    
    if (!monthlyMap[monthKey]) {
      monthlyMap[monthKey] = {
        month_key: monthKey,
        year: year,
        month: month + 1,
        month_label: `${monthNames[month]} ${year}`,
        total_opmaling: 0,
        total_ordre: 0,
        total_hours: 0,
        total_daekning: 0,
        weeks_count: 0
      };
    }
    
    monthlyMap[monthKey].total_opmaling += week.opmaling_count || 0;
    monthlyMap[monthKey].total_ordre += week.ordre_count || 0;
    monthlyMap[monthKey].total_hours += week.business_hours || 0;
    monthlyMap[monthKey].total_daekning += week.daekning || 0;
    monthlyMap[monthKey].weeks_count += 1;
  });
  
  return Object.values(monthlyMap)
    .map(m => ({
      ...m,
      kpt: m.total_hours > 0 ? m.total_opmaling / m.total_hours : 0,
      dpt: m.total_hours > 0 ? m.total_daekning / m.total_hours : 0,
      konvertering: m.total_opmaling > 0 ? (m.total_ordre / m.total_opmaling) * 100 : 0,
      opmaling_count: m.total_opmaling,
      ordre_count: m.total_ordre,
      business_hours: Math.round(m.total_hours * 10) / 10,
      daekning: m.total_daekning
    }))
    .sort((a, b) => a.month_key.localeCompare(b.month_key));
};

// Custom tooltip for charts
const ChartTooltip = ({ active, payload, viewMode, chartType }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    const label = viewMode === 'week' 
      ? `${data?.week_label} (${data?.year})`
      : data?.month_label;
    
    return (
      <div className="bg-[#1A1A1A] border border-white/20 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: <span className="font-bold">
              {entry.dataKey === 'dpt' ? formatDKK(entry.value) + ' kr/time' : 
               entry.dataKey === 'daekning' ? formatDKK(entry.value) + ' kr' :
               entry.dataKey === 'konvertering' ? entry.value?.toFixed(1) + '%' :
               entry.dataKey === 'kpt' ? entry.value?.toFixed(2) + ' kunder/time' :
               entry.dataKey === 'business_hours' ? Math.round(entry.value) + ' timer' :
               entry.value}
            </span>
          </p>
        ))}
        {chartType === 'kpt-dpt' && (
          <p className="text-white/60 text-xs mt-2">
            {Math.round(data?.opmaling_count)} opmålinger · {formatDKK(data?.daekning)} kr dækning
          </p>
        )}
        {chartType === 'konvertering' && (
          <p className="text-white/60 text-xs mt-2">
            {Math.round(data?.opmaling_count)} opmålinger · {Math.round(data?.ordre_count)} ordrer
          </p>
        )}
        {chartType === 'daekning-kpt' && (
          <p className="text-white/60 text-xs mt-2">
            {Math.round(data?.opmaling_count)} opmålinger · {data?.business_hours} timer
          </p>
        )}
        {chartType === 'timer' && (
          <p className="text-white/60 text-xs mt-2">
            {Math.round(data?.opmaling_count)} opmålinger · KPT: {data?.kpt?.toFixed(2)}
          </p>
        )}
      </div>
    );
  }
  return null;
};

// KPT and DPT Dual Line Chart Component
const KPTDPTDualLineChart = ({ data, viewMode }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxKPT = Math.max(...data.map(d => d.kpt)) * 1.2;
  const maxDPT = Math.max(...data.map(d => d.dpt)) * 1.2;
  
  const xDataKey = viewMode === 'week' ? 'week_number' : 'month_label';
  const xTickFormatter = viewMode === 'week' ? (v) => `Uge ${v}` : (v) => v;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey={xDataKey}
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={xTickFormatter}
        />
        <YAxis 
          yAxisId="left"
          domain={[0, maxKPT]}
          stroke="#3B82F6" 
          tick={{ fill: '#3B82F6', fontSize: 10 }}
          tickFormatter={(v) => v.toFixed(1)}
          width={40}
          label={{ value: 'KPT', angle: -90, position: 'insideLeft', fill: '#3B82F6', fontSize: 11 }}
        />
        <YAxis 
          yAxisId="right"
          orientation="right"
          domain={[0, maxDPT]}
          stroke="#F59E0B" 
          tick={{ fill: '#F59E0B', fontSize: 10 }}
          tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : Math.round(v)}
          width={50}
          label={{ value: 'DPT (kr)', angle: 90, position: 'insideRight', fill: '#F59E0B', fontSize: 11 }}
        />
        <Tooltip content={<ChartTooltip viewMode={viewMode} chartType="kpt-dpt" />} />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="kpt" 
          name="KPT (kunder/time)"
          stroke="#3B82F6" 
          strokeWidth={2}
          dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line 
          yAxisId="right"
          type="monotone" 
          dataKey="dpt" 
          name="DPT (kr/time)"
          stroke="#F59E0B" 
          strokeWidth={2}
          dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Konvertering Line Chart Component
const KonverteringLineChart = ({ data, viewMode }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxKonv = Math.max(...data.map(d => d.konvertering || 0)) * 1.2;
  const yMaxKonv = Math.max(maxKonv, 100);
  const maxKPT = Math.max(...data.map(d => d.kpt)) * 1.2;
  
  const xDataKey = viewMode === 'week' ? 'week_number' : 'month_label';
  const xTickFormatter = viewMode === 'week' ? (v) => `Uge ${v}` : (v) => v;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey={xDataKey}
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={xTickFormatter}
        />
        <YAxis 
          yAxisId="left"
          domain={[0, yMaxKonv]}
          stroke="#10B981" 
          tick={{ fill: '#10B981', fontSize: 10 }}
          tickFormatter={(v) => `${v.toFixed(0)}%`}
          width={50}
          label={{ value: 'Konv.', angle: -90, position: 'insideLeft', fill: '#10B981', fontSize: 11 }}
        />
        <YAxis 
          yAxisId="right"
          orientation="right"
          domain={[0, maxKPT]}
          stroke="#3B82F6" 
          tick={{ fill: '#3B82F6', fontSize: 10 }}
          tickFormatter={(v) => v.toFixed(1)}
          width={40}
          label={{ value: 'KPT', angle: 90, position: 'insideRight', fill: '#3B82F6', fontSize: 11 }}
        />
        <Tooltip content={<ChartTooltip viewMode={viewMode} chartType="konvertering" />} />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="konvertering" 
          name="Konvertering"
          stroke="#10B981" 
          strokeWidth={2}
          dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line 
          yAxisId="right"
          type="monotone" 
          dataKey="kpt" 
          name="KPT (kunder/time)"
          stroke="#3B82F6" 
          strokeWidth={2}
          dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Dækning og KPT Line Chart Component
const DaekningKPTLineChart = ({ data, viewMode }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxDaekning = Math.max(...data.map(d => d.daekning || 0)) * 1.2;
  const maxKPT = Math.max(...data.map(d => d.kpt)) * 1.2;
  
  const xDataKey = viewMode === 'week' ? 'week_number' : 'month_label';
  const xTickFormatter = viewMode === 'week' ? (v) => `Uge ${v}` : (v) => v;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey={xDataKey}
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={xTickFormatter}
        />
        <YAxis 
          yAxisId="left"
          domain={[0, maxDaekning]}
          stroke="#8B5CF6" 
          tick={{ fill: '#8B5CF6', fontSize: 10 }}
          tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : Math.round(v)}
          width={55}
          label={{ value: 'Dækning (kr)', angle: -90, position: 'insideLeft', fill: '#8B5CF6', fontSize: 11 }}
        />
        <YAxis 
          yAxisId="right"
          orientation="right"
          domain={[0, maxKPT]}
          stroke="#3B82F6" 
          tick={{ fill: '#3B82F6', fontSize: 10 }}
          tickFormatter={(v) => v.toFixed(1)}
          width={40}
          label={{ value: 'KPT', angle: 90, position: 'insideRight', fill: '#3B82F6', fontSize: 11 }}
        />
        <Tooltip content={<ChartTooltip viewMode={viewMode} chartType="daekning-kpt" />} />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="daekning" 
          name="Samlet dækning"
          stroke="#8B5CF6" 
          strokeWidth={2}
          dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
        <Line 
          yAxisId="right"
          type="monotone" 
          dataKey="kpt" 
          name="KPT (kunder/time)"
          stroke="#3B82F6" 
          strokeWidth={2}
          dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Konsulenttimer Line Chart Component
const KonsulenttimerLineChart = ({ data, viewMode }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxHours = Math.max(...data.map(d => d.business_hours || 0)) * 1.2;
  
  const xDataKey = viewMode === 'week' ? 'week_number' : 'month_label';
  const xTickFormatter = viewMode === 'week' ? (v) => `Uge ${v}` : (v) => v;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey={xDataKey}
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={xTickFormatter}
        />
        <YAxis 
          domain={[0, maxHours]}
          stroke="#EC4899" 
          tick={{ fill: '#EC4899', fontSize: 10 }}
          tickFormatter={(v) => Math.round(v)}
          width={50}
          label={{ value: 'Timer', angle: -90, position: 'insideLeft', fill: '#EC4899', fontSize: 11 }}
        />
        <Tooltip content={<ChartTooltip viewMode={viewMode} chartType="timer" />} />
        <Line 
          type="monotone" 
          dataKey="business_hours" 
          name="Konsulenttimer"
          stroke="#EC4899" 
          strokeWidth={2}
          dot={{ fill: '#EC4899', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const Kapacitetsanalyse = () => {
  const navigate = useNavigate();
  const [region, setRegion] = useState('Hele landet');
  const [viewModeKptDpt, setViewModeKptDpt] = useState('week');
  const [viewModeKonv, setViewModeKonv] = useState('week');
  const [viewModeDaekning, setViewModeDaekning] = useState('week');
  const [viewModeTimer, setViewModeTimer] = useState('week');
  const regions = ['Hele landet', 'Sjælland', 'Fyn', 'Jylland'];
  
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const regionParam = region !== 'Hele landet' ? `&region=${encodeURIComponent(region)}` : '';
      
      const response = await api.get(`/consultant-performance?weeks=26${regionParam}`);
      setPerformanceData(response.data);
    } catch (err) {
      console.error('Data fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Kunne ikke hente data. Prøv igen senere.');
      }
    } finally {
      setLoading(false);
    }
  }, [region, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartDataKptDpt = useMemo(() => {
    if (!performanceData?.weekly_data) return [];
    
    if (viewModeKptDpt === 'month') {
      return aggregateToMonthly(performanceData.weekly_data);
    }
    return performanceData.weekly_data;
  }, [performanceData, viewModeKptDpt]);

  const chartDataKonv = useMemo(() => {
    if (!performanceData?.weekly_data) return [];
    
    if (viewModeKonv === 'month') {
      return aggregateToMonthly(performanceData.weekly_data);
    }
    return performanceData.weekly_data;
  }, [performanceData, viewModeKonv]);

  const chartDataDaekning = useMemo(() => {
    if (!performanceData?.weekly_data) return [];
    
    if (viewModeDaekning === 'month') {
      return aggregateToMonthly(performanceData.weekly_data);
    }
    return performanceData.weekly_data;
  }, [performanceData, viewModeDaekning]);

  const chartDataTimer = useMemo(() => {
    if (!performanceData?.weekly_data) return [];
    
    if (viewModeTimer === 'month') {
      return aggregateToMonthly(performanceData.weekly_data);
    }
    return performanceData.weekly_data;
  }, [performanceData, viewModeTimer]);

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Navigation />
            <h1 className="text-xl font-semibold text-white">Kapacitetsanalyse</h1>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 text-white text-sm px-4 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
              data-testid="region-filter"
            >
              {regions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
      </header>
      
      <main className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/60">Indlæser data...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-400">{error}</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* First row - 2 charts side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Konsulenttimer Chart */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-white">Konsulenttimer</h2>
                    <InfoTooltip
                      title="Konsulenttimer"
                      description="Viser det samlede antal arbejdstimer for konsulenter pr. uge/måned."
                      formula="Timer = Sum af business_hours_min ÷ 60"
                      details={[
                        "Kun 'Konsulent' og 'Konsulent/montør' indgår",
                        "Konsulent/montør tælles med 60% af deres tid",
                        "Fravær (sygdom, ferie, barsel, barns sygedag) fratrækkes"
                      ]}
                      dataSource="CalendarTypeCounts (Data_klar_1)"
                    />
                  </div>
                  <div className="flex bg-white/5 rounded-lg p-1">
                    <button
                      onClick={() => setViewModeTimer('week')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeTimer === 'week' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Uge
                    </button>
                    <button
                      onClick={() => setViewModeTimer('month')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeTimer === 'month' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Måned
                    </button>
                  </div>
                </div>
                <KonsulenttimerLineChart data={chartDataTimer} viewMode={viewModeTimer} />
              </div>

              {/* KPT og DPT Chart */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-white">KPT og DPT Udvikling</h2>
                    <InfoTooltip
                      title="KPT og DPT"
                      description="Viser udviklingen i KPT (Kunder pr. time) og DPT (Dækning pr. time) over tid."
                      formula="KPT = Opmålinger ÷ Timer | DPT = Dækning ÷ Timer"
                      details={[
                        "KPT måler effektivitet (antal kunder pr. arbejdstime)",
                        "DPT måler indtjening (dækning pr. arbejdstime)",
                        "Begge justeres for konsulent/montør (60%)"
                      ]}
                      dataSource="CalendarTypeCounts + Data_klar"
                    />
                  </div>
                  <div className="flex bg-white/5 rounded-lg p-1">
                    <button
                      onClick={() => setViewModeKptDpt('week')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeKptDpt === 'week' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Uge
                    </button>
                    <button
                      onClick={() => setViewModeKptDpt('month')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeKptDpt === 'month' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Måned
                    </button>
                  </div>
                </div>
                <KPTDPTDualLineChart data={chartDataKptDpt} viewMode={viewModeKptDpt} />
              </div>
            </div>

            {/* Second row - 2 charts side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Samlet dækning og KPT Chart */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-white">Samlet dækning og KPT</h2>
                    <InfoTooltip
                      title="Samlet dækning og KPT"
                      description="Sammenligner den samlede dækning (kr) med KPT over tid."
                      formula="Dækning = Sum af daekningsbidrag fra ordrer"
                      details={[
                        "Dækning viser total indtjening pr. uge/måned",
                        "KPT viser effektiviteten parallelt",
                        "Bruges til at se om høj effektivitet giver høj indtjening"
                      ]}
                      dataSource="Data_klar (ordrer)"
                    />
                  </div>
                  <div className="flex bg-white/5 rounded-lg p-1">
                    <button
                      onClick={() => setViewModeDaekning('week')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeDaekning === 'week' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Uge
                    </button>
                    <button
                      onClick={() => setViewModeDaekning('month')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeDaekning === 'month' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Måned
                    </button>
                  </div>
                </div>
                <DaekningKPTLineChart data={chartDataDaekning} viewMode={viewModeDaekning} />
              </div>

              {/* Konvertering Chart */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-white">Konvertering og KPT</h2>
                    <InfoTooltip
                      title="Konvertering"
                      description="Viser hvor stor en andel af opmålingerne der bliver til ordrer."
                      formula="Konvertering = (Antal ordrer ÷ Antal opmålinger) × 100"
                      details={[
                        "100% = alle opmålinger bliver til ordrer",
                        "Typisk ligger konverteringen på 50-70%",
                        "Vises sammen med KPT for at se sammenhængen"
                      ]}
                      dataSource="CalendarTypeCounts + Data_klar"
                    />
                  </div>
                  <div className="flex bg-white/5 rounded-lg p-1">
                    <button
                      onClick={() => setViewModeKonv('week')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeKonv === 'week' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Uge
                    </button>
                    <button
                      onClick={() => setViewModeKonv('month')}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        viewModeKonv === 'month' 
                          ? 'bg-[#1E6F7A] text-white' 
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Måned
                    </button>
                  </div>
                </div>
                <KonverteringLineChart data={chartDataKonv} viewMode={viewModeKonv} />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Kapacitetsanalyse;
